/**
 * React Query hooks for manual readings/events.
 * React Query hooks for the corresponding entity.
 *
 * This file contains query hooks (reading data).
 * For mutation hooks (create, update, delete), see useReadingMutations.ts.
 */

import { QueryClient, useQueries, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { useMemo } from "react";
import {
  EventType,
  fetchReadingsForFieldFn,
  getLatestCurrentDate,
  isValidEvent,
  ManualDMInputResponse,
  weekMs,
} from "./readingsApiHelpers";
import { useFields } from "./useFieldQueries";

// Re-export types and utilities for backward compatibility
export type {
  EventResponse,
  InputKind,
  InputVerificationStatus,
  ManualDMInputResponse,
  ManualDMInputResponseItem,
} from "./readingsApiHelpers";
// Re-export imported values (not re-import from source to avoid duplicate declarations)
export { EventType, isValidEvent };

// Re-export mutations for backward compatibility
export {
  useCreateReading,
  useCreateReadingsBulk,
  useDeleteReading,
  useUpdateReading,
} from "./useReadingMutations";

/**
 * Fetch readings for a field.
 */
export function useReadingsForField(
  fieldId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.events.readings.byField(fieldId ?? ""),
    queryFn: () => (fieldId ? fetchReadingsForFieldFn(fieldId) : []),
    enabled: enabled && !!fieldId,
  });
}

/**
 * Get all readings across all fields with proper React Query subscriptions.
 * Uses useFields() to get field IDs and useQueries to subscribe to each field's readings.
 */
export function useAllReadings(): {
  data: ManualDMInputResponse[];
  isLoading: boolean;
} {
  const { data: fields = [], isLoading: fieldsLoading } = useFields();

  // Get all field IDs
  const fieldIds = useMemo(() => fields.map((f) => f.guid), [fields]);

  // Create queries for each field's readings
  const readingQueries = useQueries({
    queries: fieldIds.map((fieldId) => ({
      queryKey: queryKeys.events.readings.byField(fieldId),
      queryFn: () => fetchReadingsForFieldFn(fieldId),
      enabled: fieldIds.length > 0,
    })),
  });

  // Combine all readings from all queries
  const allReadings = useMemo(() => {
    const readings: ManualDMInputResponse[] = [];
    readingQueries.forEach((query) => {
      if (query.data) {
        readings.push(...query.data);
      }
    });
    return readings;
  }, [readingQueries]);

  const isLoading = fieldsLoading || readingQueries.some((q) => q.isLoading);

  return { data: allReadings, isLoading };
}

/**
 * @deprecated Use useAllReadings() instead. This function reads from cache without subscriptions.
 * Get all readings from cache (across all fields).
 */
export function useAllReadingsLegacy(): ManualDMInputResponse[] {
  const { data } = useAllReadings();
  return data;
}

/**
 * Get events for a specific field (non-rejected).
 * Uses useReadingsForField() for proper React Query subscriptions.
 */
export function useEventsForField(fieldGuid: string): ManualDMInputResponse[] {
  const { data: readings = [] } = useReadingsForField(fieldGuid);

  return useMemo(() => {
    return readings.filter(
      (reading) =>
        reading.manualDryMatterInput.verificationStatus !== "Rejected"
    );
  }, [readings]);
}

/**
 * Get events for multiple fields.
 * Uses useQueries for proper React Query subscriptions to each field's readings.
 */
export function useEventsForFields(
  fieldGuids: string[],
  includedKinds: string = "user"
): Record<string, ManualDMInputResponse[]> {
  // Create queries for each field's readings
  const readingQueries = useQueries({
    queries: fieldGuids.map((fieldId) => ({
      queryKey:
        includedKinds === "user"
          ? queryKeys.events.readings.byField(fieldId)
          : [...queryKeys.events.readings.byField(fieldId), includedKinds],
      queryFn: () => fetchReadingsForFieldFn(fieldId, includedKinds),
      enabled: fieldGuids.length > 0,
    })),
  });

  return useMemo(() => {
    const result: Record<string, ManualDMInputResponse[]> = {};

    fieldGuids.forEach((guid, index) => {
      const queryData = readingQueries[index]?.data || [];
      result[guid] = queryData.filter(
        (reading) =>
          reading.manualDryMatterInput.verificationStatus !== "Rejected"
      );
    });

    return result;
  }, [fieldGuids, readingQueries]);
}

/**
 * Get readings grouped by field guid.
 * Uses useAllReadings() for proper React Query subscriptions.
 */
export function useReadingsByFieldGuid(): Record<
  string,
  ManualDMInputResponse[]
> {
  const { data: allReadings } = useAllReadings();

  return useMemo(() => {
    const result: Record<string, ManualDMInputResponse[]> = {};

    allReadings.forEach((reading) => {
      if (reading.manualDryMatterInput.verificationStatus !== "Rejected") {
        const fieldGuid = reading.manualDryMatterInput.field;
        if (!result[fieldGuid]) {
          result[fieldGuid] = [];
        }
        result[fieldGuid].push(reading);
      }
    });

    return result;
  }, [allReadings]);
}

/**
 * Get latest manual reading date for fields.
 * Uses useEventsForFields() for proper React Query subscriptions.
 */
export function useLatestManualReadingDate(
  fieldGuids: string[]
): Date | undefined {
  const eventsByField = useEventsForFields(fieldGuids);

  return useMemo(() => {
    let latestManualReadingDate: Date | undefined = undefined;

    Object.values(eventsByField)
      .flat()
      .forEach((reading: ManualDMInputResponse) => {
        if (reading.manualDryMatterInput.event) {
          if (
            reading.manualDryMatterInput.event.eventType ===
              EventType.GrazingEvent ||
            reading.manualDryMatterInput.event.eventType ===
              EventType.PartialGrazingEvent
          ) {
            const currentLatestDate = getLatestCurrentDate(reading);
            if (currentLatestDate) {
              if (!latestManualReadingDate) {
                latestManualReadingDate = currentLatestDate;
              }
              if (latestManualReadingDate < currentLatestDate) {
                latestManualReadingDate = currentLatestDate;
              }
            }
          }
        }
      });

    return latestManualReadingDate;
  }, [eventsByField]);
}

/**
 * Get latest move-in date for fields.
 * Uses useEventsForFields() for proper React Query subscriptions.
 */
export function useLatestMoveInDate(fieldGuids: string[]): Date | undefined {
  const eventsByField = useEventsForFields(fieldGuids);

  return useMemo(() => {
    let latestMoveInDate: Date | undefined = undefined;

    Object.values(eventsByField)
      .flat()
      .forEach((reading: ManualDMInputResponse) => {
        if (reading.manualDryMatterInput.event) {
          if (
            reading.manualDryMatterInput.event.eventType ===
              EventType.GrazingEvent ||
            reading.manualDryMatterInput.event.eventType ===
              EventType.PartialGrazingEvent
          ) {
            const dateTimeIn = reading.manualDryMatterInput.event.dateTimeIn;
            if (dateTimeIn) {
              const moveInDate = new Date(dateTimeIn);
              if (!latestMoveInDate || latestMoveInDate < moveInDate) {
                latestMoveInDate = moveInDate;
              }
            }
          }
        }
      });

    return latestMoveInDate;
  }, [eventsByField]);
}

/**
 * Get all proposed events for one week sorted.
 * Uses useAllReadings() for proper React Query subscriptions.
 */
export function useProposedEventsForOneWeek(): ManualDMInputResponse[] {
  const { data: allReadings } = useAllReadings();

  return useMemo(() => {
    const timestampOneWeekAgo = new Date().getTime() - weekMs;
    const validEventTypes = [
      EventType.GrazingEvent,
      EventType.PartialGrazingEvent,
      EventType.SilageEvent,
    ];

    const filteredEvents = allReadings.filter((reading) => {
      const { event, verificationStatus } = reading.manualDryMatterInput;

      if (validEventTypes.indexOf(event.eventType) === -1) {
        return false;
      }

      if (verificationStatus !== "Proposed") {
        return false;
      }

      if (
        event.dateTimeIn &&
        Date.parse(event.dateTimeIn) < timestampOneWeekAgo
      ) {
        return false;
      }

      if (
        event.dateTimeCut &&
        Date.parse(event.dateTimeCut) < timestampOneWeekAgo
      ) {
        return false;
      }

      if (!isValidEvent(reading)) {
        return false;
      }

      return true;
    });

    filteredEvents.sort((a, b) => {
      const eventA = a.manualDryMatterInput.event;
      const eventB = b.manualDryMatterInput.event;
      if (eventA.dateTimeIn && eventB.dateTimeIn) {
        return Date.parse(eventB.dateTimeIn) - Date.parse(eventA.dateTimeIn);
      } else if (eventA.dateTimeIn && eventB.dateTimeCut) {
        return Date.parse(eventB.dateTimeCut) - Date.parse(eventA.dateTimeIn);
      } else if (eventB.dateTimeIn && eventA.dateTimeCut) {
        return Date.parse(eventB.dateTimeIn) - Date.parse(eventA.dateTimeCut);
      } else {
        return -1;
      }
    });

    return filteredEvents;
  }, [allReadings]);
}

/**
 * Get recent proposed events for a field.
 */
export function useRecentProposedEventsForField(
  fieldGuid: string
): ManualDMInputResponse[] {
  const proposedEvents = useProposedEventsForOneWeek();

  return useMemo(() => {
    return proposedEvents.filter(
      (event) => event.manualDryMatterInput.field === fieldGuid
    );
  }, [proposedEvents, fieldGuid]);
}

/**
 * Prefetch readings for a field (used during initialization).
 */
export async function fetchReadingsForFieldQuery(
  fieldId: string,
  queryClient: QueryClient
): Promise<ManualDMInputResponse[]> {
  const readings = await fetchReadingsForFieldFn(fieldId);
  queryClient.setQueryData(
    queryKeys.events.readings.byField(fieldId),
    readings
  );
  return readings;
}

/**
 * Reset readings query cache.
 */
export function resetReadingsQueryCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.events.readings.all });
}
